import { Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DataProvider } from './contexts/DataContext';
import { AuthProvider } from './contexts/AuthContext';
import { UserCompanyProvider } from './contexts/UserCompanyContext';
import { ErrorBoundary } from './components/errors/ErrorBoundary';
import { useRouteChangeTracker } from './hooks/useRouteChangeTracker';
import { AppHead } from './components/seo/AppHead';
import { checkAccessibility } from './utils/accessibility';
import { trackPageView } from './utils/analytics';
import { appRoutes } from './routes/routes';
import { AppLoadingFallback } from './components/app/AppLoadingFallback';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';

function App() {
  // Track route changes for analytics
  useRouteChangeTracker();
  const location = useLocation();
  
  // Debug logging for app lifecycle
  useEffect(() => {
    console.log('📍 App.tsx mounted');
    console.log('📍 App environment:', {
      dev: import.meta.env.DEV,
      mode: import.meta.env.MODE,
      pathname: location.pathname
    });
    return () => console.log('📍 App.tsx unmounted');
  }, []);
  
  // Run accessibility checks in development
  useEffect(() => {
    if (import.meta.env.DEV) {
      // Wait for page to render before checking
      const timeoutId = setTimeout(() => {
        checkAccessibility();
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname]);
  
  // Track page views for analytics
  useEffect(() => {
    console.log('🔄 Route changed to:', location.pathname);
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  return (
    <ErrorBoundary>
      <AppHead />
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <UserCompanyProvider>
            <UserProvider>
              <DataProvider>
                <NotificationProvider>
                  <Suspense fallback={<AppLoadingFallback />}>
                    <Routes>
                      {appRoutes.map((route) => (
                        <Route 
                          key={route.path} 
                          path={route.path} 
                          element={route.element} 
                        />
                      ))}
                    </Routes>
                  </Suspense>
                  
                  <SonnerToaster />
                </NotificationProvider>
              </DataProvider>
            </UserProvider>
          </UserCompanyProvider>
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
